// ============================================
// FORMATADORES GLOBAIS — CLICK SORVETES
// ============================================
// Estrutura organizada e documentada por categoria
// Segue padrão de nomenclatura unificada e funções utilitárias públicas
// ============================================

/* ============================================================
   💰 CATEGORIA 1 — MOEDA E NÚMEROS
   ============================================================ */

/**
 * Converte número em formato BRL.
 * @param {number} [v=0] - Valor numérico a ser formatado.
 * @returns {string} Valor formatado em moeda brasileira.
 */
export const brl = (v = 0) =>
  (Number.isFinite(v) ? v : 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

/**
 * Retorna porcentagem formatada com base opcional.
 * @param {number} v - Valor parcial.
 * @param {number} base - Valor total usado como base.
 * @returns {string} Porcentagem formatada (1 casa decimal).
 */
export const pctStr = (v = 0, base = 0) =>
  base > 0 ? ((v / base) * 100).toFixed(1) : "0.0";

/**
 * Converte string pt-BR para número.
 * @param {string|number} s - String numérica (ex: "1.234,56").
 * @returns {number} Valor numérico convertido.
 */
export const toNumber = (s) => {
  if (typeof s === "number") return s;
  if (!s) return 0;
  return Number(String(s).replace(/\./g, "").replace(",", ".")) || 0;
};

/**
 * Converte valor monetário pt-BR em número.
 * @param {string|number} value - Valor no formato monetário.
 * @returns {number} Valor numérico.
 */
export function parseMoney(value) {
  const n = parseFloat(
    String(value || "").replace(/\./g, "").replace(",", "."),
  );
  return isNaN(n) ? 0 : n;
}

/* --------- Máscaras com caret (para inputs reativos) --------- */

/**
 * Conta quantos dígitos existem à direita do cursor (caret).
 * @param {string} value
 * @param {number} caretPos
 * @returns {number}
 */
function digitCountRightOfCaret(value, caretPos) {
  return (value.slice(caretPos).match(/\d/g) || []).length;
}

/**
 * Retorna a posição correta do caret após reformatação.
 * @param {string} formatted
 * @param {number} targetDigitsRight
 * @returns {number}
 */
function caretFromDigitsRight(formatted, targetDigitsRight) {
  if (targetDigitsRight <= 0) return formatted.length;
  let count = 0;
  for (let i = formatted.length - 1; i >= 0; i--) {
    if (/\d/.test(formatted[i])) {
      count++;
      if (count === targetDigitsRight) return i;
    }
  }
  return 0;
}

/**
 * Aplica máscara de moeda a partir dos dígitos inseridos.
 * @param {string} digits - Somente números.
 * @returns {string} Valor formatado como "1.234,56".
 */
function maskCurrencyFromDigits(digits) {
  if (!digits) return "";
  if (digits.length === 1) digits = "0" + digits;
  const inteiroRaw = digits.slice(0, -2).replace(/^0+(?=\d)/, "");
  const centavos = digits.slice(-2);
  const inteiroFmt = inteiroRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${inteiroFmt || "0"},${centavos}`;
}

/**
 * Máscara de moeda com caret estável (input).
 * @param {InputEvent} e - Evento do input.
 */
export function formatCurrencyInput(e) {
  if (!e || !e.target) return;
  const input = e.target;
  const caret = input.selectionStart ?? input.value.length;
  const digitsRight = digitCountRightOfCaret(input.value, caret);
  let digits = input.value.replace(/\D/g, "");
  if (digits.length === 0) {
    input.value = "";
    return;
  }
  const formatted = maskCurrencyFromDigits(digits);
  input.value = formatted;
  const newPos = caretFromDigitsRight(formatted, digitsRight);
  input.setSelectionRange(newPos, newPos);
}

/**
 * Máscara de percentual com caret estável (input).
 * @param {InputEvent} e - Evento do input.
 */
export function formatPercentInput(e) {
  if (!e || !e.target) return;
  const input = e.target;
  const caret = input.selectionStart ?? input.value.length;
  const digitsRight = digitCountRightOfCaret(input.value, caret);
  let digits = input.value.replace(/\D/g, "");
  if (digits.length === 0) {
    input.value = "";
    return;
  }
  const inteiroRaw = digits.slice(0, -2).replace(/^0+(?=\d)/, "");
  const decimais = digits.slice(-2);
  const inteiroFmt = inteiroRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const formatted = `${inteiroFmt || "0"},${decimais}`;
  input.value = formatted;
  const newPos = caretFromDigitsRight(formatted, digitsRight);
  input.setSelectionRange(newPos, newPos);
}

/**
 * Alias alternativo para compatibilidade.
 */
export { brl as formatarMoeda };

/* ============================================================
   🔤 CATEGORIA 2 — TEXTO E STRINGS
   ============================================================ */

/**
 * Remove acentos e caracteres especiais de uma string.
 * @param {string} [texto=""] - Texto original.
 * @returns {string} Texto limpo sem acentuação.
 */
export function removerAcentos(texto = "") {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/gi, "")
    .trim();
}

/**
 * Gera slug URL-friendly a partir de um texto.
 * @param {string} [text=""]
 * @returns {string} Slug formatado (ex: "click-sorvetes").
 */
export const slugify = (text = "") =>
  String(text)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w]+/g, "-")
    .replace(/--+/g, "-")
    .trim();

/**
 * Capitaliza cada palavra (primeira letra maiúscula).
 * @param {string} [texto=""]
 * @returns {string} Texto capitalizado.
 */
export function capitalizarTexto(texto = "") {
  return texto
    .toLowerCase()
    .split(" ")
    .map((palavra) => palavra.charAt(0).toUpperCase() + palavra.slice(1))
    .join(" ");
}

/* ============================================================
   🪪 CATEGORIA 3 — DOCUMENTOS E TELEFONE
   ============================================================ */

/**
 * Formata CPF com pontos e traço.
 * @param {string} [cpf=""]
 * @returns {string} CPF formatado (ex: 123.456.789-09)
 */
export function formatarCPF(cpf = "") {
  cpf = cpf.replace(/\D/g, "").slice(0, 11);
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

/**
 * Formata CNPJ com pontos, barra e traço.
 * @param {string} [cnpj=""]
 * @returns {string} CNPJ formatado (ex: 12.345.678/0001-90)
 */
export function formatarCNPJ(cnpj = "") {
  cnpj = cnpj.replace(/\D/g, "").slice(0, 14);
  return cnpj.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
}

/**
 * Formata telefone (celular ou fixo) com DDD.
 * @param {string} [numero=""]
 * @returns {string} Telefone formatado (ex: (11) 98765-4321)
 */
export function formatarTelefone(numero = "") {
  numero = numero.replace(/\D/g, "").slice(0, 11);
  if (numero.length <= 10) {
    return numero.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  } else {
    return numero.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
  }
}

/**
 * Extrai apenas os dígitos numéricos de uma string.
 * @param {string} [str=""]
 * @returns {string} Dígitos concatenados.
 */
export function extrairNumeros(str = "") {
  return str.replace(/\D/g, "");
}

/* ============================================================
   📅 CATEGORIA 4 — DATAS
   ============================================================ */

/**
 * Formata data no formato dia/mês extenso.
 * @param {string} str - String de data (ex: "25/12/2025").
 * @returns {string} Data formatada (ex: "25 de dezembro").
 */
export const formatarData = (str) => {
  if (!str) return "";
  const partes = str.split("/");
  const [dia, mes, ano] = [
    parseInt(partes[0]),
    parseInt(partes[1]) - 1,
    partes[2] ? parseInt(partes[2]) : new Date().getFullYear(),
  ];
  const data = new Date(ano, mes, dia);
  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
};


/**
 * ✂️ Limita o número de caracteres de um texto, adicionando "..." se ultrapassar o limite.
 * @param {string} texto - Texto original
 * @param {number} limite - Número máximo de caracteres permitidos
 * @returns {string} Texto truncado
 */
export function limitarTexto(texto, limite = 100) {
  if (!texto || typeof texto !== "string") return "";
  const textoLimpo = texto.trim();
  if (textoLimpo.length <= limite) return textoLimpo;
  return textoLimpo.slice(0, limite).trimEnd() + " . . .";
}
